import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/app/Header";
import { TradingStyleSelector } from "../components/app/TradingStyleSelector";
import { AnalysisQuota } from "../components/app/AnalysisQuota";
import { UpgradeModal } from "../components/app/UpgradeModal";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api, fileToBase64 } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Upload, ArrowUpRight, ArrowDownRight, MinusSquare, Loader2, ImageIcon, Trash2, AlertTriangle, Clock, Crown } from "lucide-react";

const DirectionBadge = ({ direction, t }) => {
  if (direction === "BULLISH" || direction === "UP") return <span className="inline-flex items-center gap-1 text-bull font-mono"><ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />{t("direction_BULLISH")}</span>;
  if (direction === "BEARISH" || direction === "DOWN") return <span className="inline-flex items-center gap-1 text-bear font-mono"><ArrowDownRight className="h-3.5 w-3.5" strokeWidth={2} />{t("direction_BEARISH")}</span>;
  return <span className="inline-flex items-center gap-1 text-neutral-400 font-mono"><MinusSquare className="h-3.5 w-3.5" strokeWidth={2} />{t("direction_NEUTRAL")}</span>;
};

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [symbol, setSymbol] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [notes, setNotes] = useState("");
  const [tradingStyle, setTradingStyle] = useState("DAY_TRADING");
  const [analyzing, setAnalyzing] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastUsed, setLastUsed] = useState({ symbol: "", timeframe: "" });
  const [quota, setQuota] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  // Handle Stripe redirect + watchlist symbol pre-fill
  useEffect(() => {
    // Pre-fill from watchlist "Analyze →" link
    const urlSymbol = params.get("symbol");
    if (urlSymbol) {
      setSymbol(urlSymbol.toUpperCase());
      params.delete("symbol"); params.delete("market_type");
      setParams(params, { replace: true });
    }
    if (params.get("success") === "true") {
      const plan = params.get("plan") || "TRADER";
      toast.success(t("success_subscribed").replace("{plan}", plan));
      // Refresh user + quota — webhook may take a moment
      const reload = async () => {
        await refresh();
        const { data } = await api.get("/billing/quota");
        setQuota(data);
      };
      // poll a few times
      reload();
      setTimeout(reload, 2500);
      setTimeout(reload, 6000);
      params.delete("success"); params.delete("plan"); params.delete("session_id");
      setParams(params, { replace: true });
    } else if (params.get("canceled") === "true") {
      toast(t("cancel_back"));
      params.delete("canceled");
      setParams(params, { replace: true });
    }
  }, []);

  // hydrate preferences (DB > localStorage > defaults). URL ?symbol= ALWAYS wins over saved prefs.
  useEffect(() => {
    if (!user) return;
    const p = user.preferences || {};
    const lsStyle = localStorage.getItem("candle_style");
    const lsSymbol = localStorage.getItem("candle_symbol");
    const lsTf = localStorage.getItem("candle_tf");
    const style = p.trading_style || lsStyle || "DAY_TRADING";
    setTradingStyle(style);
    // URL ?symbol= was set by the prior useEffect; only fall back if symbol still empty
    setSymbol((cur) => cur || p.favorite_symbol || lsSymbol || "");
    const tf = p.favorite_timeframe || lsTf || "";
    setTimeframe(tf);
    setLastUsed({ symbol: p.favorite_symbol || lsSymbol || "", timeframe: tf });
  }, [user]);

  // persist style to localStorage when changed
  useEffect(() => { if (tradingStyle) localStorage.setItem("candle_style", tradingStyle); }, [tradingStyle]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/analyses");
      setHistory(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (user) loadHistory(); }, [user]);

  // Load quota on mount and after analysis
  const loadQuota = async () => {
    try {
      const { data } = await api.get("/billing/quota");
      setQuota(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (user) loadQuota(); }, [user]);

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error(t("toast_only_image")); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error(t("toast_too_big")); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const analyze = async () => {
    if (!file) { toast.error(t("toast_upload_first")); return; }
    // Pre-check quota
    if (quota && !quota.unlimited && (quota.remaining ?? 0) <= 0) {
      setShowUpgrade(true);
      return;
    }
    setAnalyzing(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/analyze", {
        image_base64: b64,
        symbol: symbol || null,
        timeframe: timeframe || null,
        notes: notes || null,
        trading_style: tradingStyle,
        language: lang,
      });
      if (symbol) localStorage.setItem("candle_symbol", symbol);
      if (timeframe) localStorage.setItem("candle_tf", timeframe);
      toast.success(t("toast_analysis_complete"));
      loadQuota();
      navigate(`/analysis/${data.analysis_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 || (typeof detail === "object" && detail?.code === "QUOTA_EXCEEDED")) {
        setShowUpgrade(true);
        loadQuota();
      } else {
        toast.error((typeof detail === "string" ? detail : null) || t("toast_analysis_failed"));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const resetUpload = () => { setFile(null); setPreview(null); if (fileInput.current) fileInput.current.value = ""; };

  const removeOne = async (id) => {
    try {
      await api.delete(`/analyses/${id}`);
      setHistory((h) => h.filter((x) => x.analysis_id !== id));
    } catch { toast.error(t("toast_delete_failed")); }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-bull" /></div>;
  }

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Header />
      <main className="mx-auto max-w-7xl px-6 md:px-8 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <span className="label-mono">{t("dashboard_terminal")} {user.email}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter">
              {t("welcome")}, {user.name?.split(" ")[0] || t("trader_default")}.<span className="text-bull">_</span>
            </h1>
          </div>
          <div className="flex items-center gap-3 font-mono text-xs flex-wrap">
            <AnalysisQuota quota={quota} />
            <div className="px-3 h-9 hidden md:flex items-center gap-2 border border-white/10 bg-white/[0.02]">
              <span className="h-1.5 w-1.5 rounded-full bg-bull animate-pulse-glow" />
              <span className="text-neutral-400">CLAUDE_SONNET_4.5</span>
            </div>
          </div>
        </div>

        {/* Trading style selector */}
        <TradingStyleSelector value={tradingStyle} onChange={setTradingStyle} />

        <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />

        {/* Bento */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8" data-testid="upload-card">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`relative surface-card p-8 min-h-[360px] flex flex-col items-center justify-center border-2 border-dashed transition-colors ${dragOver ? "border-bull/60 bg-bull/5" : "border-white/10"}`}
              data-testid="drop-zone"
            >
              {!preview ? (
                <div className="text-center max-w-md">
                  <div className="inline-flex h-14 w-14 items-center justify-center border border-white/10 bg-white/[0.03] mb-5">
                    <Upload className="h-6 w-6 text-bull" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-medium">{t("drop_title")}</h3>
                  <p className="mt-2 text-sm text-neutral-400">{t("drop_desc")}</p>
                  <Button onClick={() => fileInput.current?.click()} className="mt-5 rounded-none bg-white text-black hover:bg-neutral-200" data-testid="browse-file-btn">
                    {t("browse_btn")}
                  </Button>
                  <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="file-input" />
                </div>
              ) : (
                <div className="w-full">
                  <div className="relative w-full aspect-[16/9] border border-white/10 bg-black overflow-hidden">
                    <img src={preview} alt="chart" className="w-full h-full object-contain" data-testid="preview-image" />
                    {analyzing && <div className="scanline-overlay" />}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-xs text-neutral-500 truncate max-w-[60%]">{file?.name}</span>
                    <Button variant="ghost" size="sm" onClick={resetUpload} className="text-neutral-400 hover:text-bear" data-testid="clear-upload-btn">
                      {t("clear_btn")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-4">
            <div className="surface-card p-6 space-y-4">
              <span className="label-mono">{t("ctx_label")}</span>
              <div className="space-y-3">
                <div>
                  <label className="label-mono">{t("field_symbol")}</label>
                  <Input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder={t("placeholder_symbol")}
                    className="mt-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
                    data-testid="dashboard-symbol-input" />
                  {(lastUsed.symbol || lastUsed.timeframe) && (
                    <div className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] text-neutral-500" data-testid="last-used-hint">
                      <Clock className="h-3 w-3" strokeWidth={1.5} />
                      <span>{t("last_used")}: {lastUsed.symbol || "—"}{lastUsed.timeframe ? ` · ${lastUsed.timeframe}` : ""}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="label-mono">{t("field_timeframe")}</label>
                  <Input value={timeframe} onChange={(e) => setTimeframe(e.target.value)} placeholder={t("placeholder_timeframe")}
                    className="mt-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
                    data-testid="timeframe-input" />
                </div>
                <div>
                  <label className="label-mono">{t("field_notes")}</label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("placeholder_notes")}
                    className="mt-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 focus-visible:ring-bull/40 focus-visible:border-bull/40"
                    data-testid="notes-input" />
                </div>
              </div>
              <Button onClick={analyze} disabled={!file || analyzing} className="w-full rounded-none bg-bull text-black hover:bg-bull/90 h-11 font-medium disabled:opacity-30" data-testid="analyze-btn">
                {analyzing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("scanning")}</> : t("analyze_btn")}
              </Button>
              <div className="flex items-start gap-2 text-[11px] text-neutral-500 font-mono leading-relaxed">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-warn shrink-0" strokeWidth={2} />
                <span>{t("risk_warning")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* History */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <span className="label-mono">{t("history_label")}</span>
            <span className="font-mono text-xs text-neutral-500">{history.length} {t("history_entries")}</span>
          </div>
          {history.length === 0 ? (
            <div className="surface-card p-10 text-center" data-testid="empty-history">
              <ImageIcon className="h-6 w-6 mx-auto text-neutral-600" strokeWidth={1.5} />
              <p className="mt-3 text-sm text-neutral-500">{t("history_empty")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="history-grid">
              {history.map((a) => (
                <div key={a.analysis_id} className="surface-card overflow-hidden group cursor-pointer transition-all" data-testid={`history-card-${a.analysis_id}`} onClick={() => navigate(`/analysis/${a.analysis_id}`)}>
                  {a.thumbnail && (
                    <div className="aspect-[16/9] bg-black border-b border-white/10 overflow-hidden">
                      <img src={a.thumbnail} alt="chart" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                  <div className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <DirectionBadge direction={a.direction} t={t} />
                      <span className="font-mono text-xs text-neutral-500">{new Date(a.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center justify-between font-mono text-sm">
                      <span className="text-neutral-300">{a.symbol || "—"} {a.timeframe ? `· ${a.timeframe}` : ""}</span>
                      <span className={a.direction === "BULLISH" ? "text-bull" : a.direction === "BEARISH" ? "text-bear" : "text-neutral-400"}>{a.confidence}%</span>
                    </div>
                    <p className="text-xs text-neutral-400 line-clamp-2 leading-relaxed">{a.analysis_summary || a.summary || ""}</p>
                    <div className="pt-2 flex justify-end">
                      <button onClick={(e) => { e.stopPropagation(); removeOne(a.analysis_id); }} className="text-neutral-600 hover:text-bear transition-colors" data-testid={`delete-${a.analysis_id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
